import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Minus, Plus, ShoppingCart, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { money, validatePromo } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, updateQty, removeItem, subtotal, promo, setPromo, discount, shipping, total } = useCart();
  const [promoInput, setPromoInput] = useState("");
  const [checking, setChecking] = useState(false);

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setChecking(true);
    try {
      const res = await validatePromo(promoInput, subtotal);
      if (res.valid) {
        setPromo({ code: res.code, percent: res.percent });
        toast.success(`${res.code} applied — ${res.percent}% off`);
      } else {
        setPromo(null);
        toast.error("Invalid promo code");
      }
    } catch {
      toast.error("Could not validate promo");
    }
    setChecking(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="cart-backdrop"
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md"
          />
          <motion.aside
            key="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-voltaic-line bg-voltaic-bg"
          >
            <div className="flex items-center justify-between border-b border-voltaic-line px-6 py-5">
              <h2 className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-wide">
                <ShoppingCart size={17} className="text-voltaic-cyan" /> Your cart
              </h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setIsOpen(false)}
                aria-label="Close cart"
                className="grid h-9 w-9 place-items-center rounded-full border border-voltaic-line text-voltaic-muted transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
              >
                <X size={15} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center" data-testid="cart-empty-state">
                <p className="font-display text-xl font-bold">Nothing in the vault yet.</p>
                <p className="text-sm text-voltaic-muted">Browse the drop and add something electric.</p>
                <button
                  data-testid="cart-empty-browse"
                  onClick={() => setIsOpen(false)}
                  className="mt-2 rounded-full border border-voltaic-linebright px-6 py-2.5 text-sm font-semibold transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan"
                >
                  Keep browsing
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
                  {items.map((i) => (
                    <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 rounded-2xl border border-voltaic-line bg-voltaic-surface p-3">
                      <img src={i.image} alt={i.name} className="h-20 w-20 shrink-0 rounded-xl object-cover" />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="truncate font-semibold">{i.name}</p>
                          <button
                            data-testid={`cart-remove-${i.product_id}`}
                            onClick={() => removeItem(i.product_id)}
                            aria-label={`Remove ${i.name}`}
                            className="text-voltaic-muted transition-colors hover:text-voltaic-pink"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <p className="mt-0.5 font-mono text-xs text-voltaic-muted">{money(i.price)} each</p>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <div className="flex items-center rounded-full border border-voltaic-line">
                            <button
                              data-testid={`cart-qty-minus-${i.product_id}`}
                              onClick={() => updateQty(i.product_id, i.quantity - 1)}
                              className="grid h-8 w-8 place-items-center text-voltaic-muted hover:text-voltaic-ink"
                              aria-label="Decrease"
                            >
                              <Minus size={12} />
                            </button>
                            <span data-testid={`cart-qty-${i.product_id}`} className="w-7 text-center font-mono text-sm">{i.quantity}</span>
                            <button
                              data-testid={`cart-qty-plus-${i.product_id}`}
                              onClick={() => updateQty(i.product_id, i.quantity + 1)}
                              className="grid h-8 w-8 place-items-center text-voltaic-muted hover:text-voltaic-ink"
                              aria-label="Increase"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                          <p className="font-display font-bold">{money(i.price * i.quantity)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4 border-t border-voltaic-line px-6 py-5">
                  <div className="flex gap-2">
                    <input
                      data-testid="cart-promo-input"
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value)}
                      placeholder="Promo code (try VOLTAIC20)"
                      className="field flex-1 !py-2.5 text-sm"
                    />
                    <button
                      data-testid="cart-promo-apply"
                      onClick={applyPromo}
                      disabled={checking}
                      className="rounded-xl border border-voltaic-linebright px-4 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors hover:border-voltaic-cyan hover:text-voltaic-cyan disabled:opacity-50"
                    >
                      {checking ? "..." : "Apply"}
                    </button>
                  </div>
                  {subtotal < 999 && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-voltaic-muted">
                      Add {money(999 - subtotal)} more for free shipping
                    </p>
                  )}
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between text-voltaic-muted"><span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
                    {discount > 0 && (
                      <div className="flex justify-between text-voltaic-green"><span>Promo {promo.code}</span><span data-testid="cart-discount">-{money(discount)}</span></div>
                    )}
                    <div className="flex justify-between text-voltaic-muted"><span>Shipping</span><span data-testid="cart-shipping">{shipping === 0 ? "Free" : money(shipping)}</span></div>
                    <div className="flex justify-between border-t border-voltaic-line pt-2.5 font-display text-lg font-bold"><span>Total</span><span data-testid="cart-total">{money(total)}</span></div>
                  </div>
                  <button
                    data-testid="cart-checkout-button"
                    onClick={() => { setIsOpen(false); window.location.assign("/checkout"); }}
                    className="group flex w-full items-center justify-center gap-2 rounded-full bg-voltaic-cyan py-3.5 font-semibold text-voltaic-bg transition-shadow duration-300 hover:glow-cyan"
                  >
                    Checkout
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
